import React, { useEffect } from 'react';
import { useApp } from '../../context/AppContext.tsx';

export default function SEOAndIntegrations() {
  const { settings, currentPage, currentArticleSlug, currentProductSlug, articles, products, selectedProduct } = useApp();

  const currentArticle = currentPage === 'article-detail' && currentArticleSlug
    ? articles.find((a) => a.slug === currentArticleSlug || a.id === currentArticleSlug)
    : null;

  const currentActiveProduct = currentPage === 'product-detail' && currentProductSlug
    ? products.find((p) => p.slug === currentProductSlug || p.id === currentProductSlug)
    : selectedProduct;

  useEffect(() => {
    if (!settings) return;

    // 1. Google Search Console Meta Verification Tag
    const gscCode = settings.integrations?.googleSearchConsole?.trim() || '';
    let metaGsc = document.querySelector('meta[name="google-site-verification"]');
    if (gscCode) {
      let contentVal = gscCode;
      // If user pasted full <meta name="google-site-verification" content="..." />
      const match = gscCode.match(/content=["']([^"']+)["']/i);
      if (match) {
        contentVal = match[1];
      }

      if (!metaGsc) {
        metaGsc = document.createElement('meta');
        metaGsc.setAttribute('name', 'google-site-verification');
        document.head.appendChild(metaGsc);
      }
      metaGsc.setAttribute('content', contentVal);
    } else if (metaGsc) {
      metaGsc.remove();
    }

    // 2. Google Discover & Search Robots Directive
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    // Google Discover requirement: max-image-preview:large
    metaRobots.setAttribute('content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    let metaGooglebot = document.querySelector('meta[name="googlebot"]');
    if (!metaGooglebot) {
      metaGooglebot = document.createElement('meta');
      metaGooglebot.setAttribute('name', 'googlebot');
      document.head.appendChild(metaGooglebot);
    }
    metaGooglebot.setAttribute('content', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    // 3. Google Analytics (gtag.js) Injection
    const gaId = settings.integrations?.googleAnalytics?.trim() || '';
    let scriptGa1 = document.getElementById('google-analytics-script') as HTMLScriptElement | null;
    let scriptGa2 = document.getElementById('google-analytics-init') as HTMLScriptElement | null;

    if (gaId) {
      let cleanGaId = gaId;
      const matchGa = gaId.match(/G-[A-Z0-9_]+/i);
      if (matchGa) {
        cleanGaId = matchGa[0].toUpperCase();
      } else {
        cleanGaId = gaId.replace(/[^a-zA-Z0-9_-]/g, '');
      }

      if (cleanGaId) {
        if (!scriptGa1) {
          scriptGa1 = document.createElement('script');
          scriptGa1.id = 'google-analytics-script';
          scriptGa1.async = true;
          document.head.appendChild(scriptGa1);
        }
        scriptGa1.src = `https://www.googletagmanager.com/gtag/js?id=${cleanGaId}`;

        if (!scriptGa2) {
          scriptGa2 = document.createElement('script');
          scriptGa2.id = 'google-analytics-init';
          document.head.appendChild(scriptGa2);
        }
        scriptGa2.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${cleanGaId}', { page_path: window.location.pathname });
        `;
      }
    } else {
      if (scriptGa1) scriptGa1.remove();
      if (scriptGa2) scriptGa2.remove();
    }

    // 4. Google AdSense (adsbygoogle.js) Injection
    const adsenseId = settings.integrations?.googleAdsense?.trim() || '';
    let scriptAd = document.getElementById('google-adsense-script') as HTMLScriptElement | null;

    if (adsenseId) {
      let pubId = adsenseId;
      const pubMatch = adsenseId.match(/ca-pub-\d+/i);
      if (pubMatch) {
        pubId = pubMatch[0].toLowerCase();
      }

      if (pubId.includes('ca-pub-')) {
        if (!scriptAd) {
          scriptAd = document.createElement('script');
          scriptAd.id = 'google-adsense-script';
          scriptAd.async = true;
          scriptAd.crossOrigin = 'anonymous';
          document.head.appendChild(scriptAd);
        }
        scriptAd.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${pubId}`;
      }
    } else if (scriptAd) {
      scriptAd.remove();
    }

    // 5. Dynamic Page Titles, Favicon & Social Media OpenGraph Meta Tags (WhatsApp, FB, Twitter, Telegram Thumbnail)
    const siteTitle = (settings.siteName || '').trim() || 'Website';
    let currentOgTitle = siteTitle;
    let currentOgDesc = settings.seo?.metaDescription || settings.tagline || `${siteTitle} - Solusi Terpadu dan Profesional`;
    let currentOgImage = settings.seo?.ogImage || settings.logoUrl || 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=1200&q=80';
    let currentOgType = 'website';
    let currentKeywords = settings.seo?.metaKeywords || `${siteTitle}, layanan terpercaya, berita, informasi`;
    let pageCanonicalPath = '';
    let breadcrumbItems: Array<{ name: string; url: string }> = [
      { name: 'Home', url: '/' }
    ];

    if (currentArticle) {
      currentOgTitle = currentArticle.metaTitle || currentArticle.title;
      currentOgDesc = currentArticle.metaDescription || currentArticle.summary || currentArticle.title;
      currentOgImage = currentArticle.coverImage || currentOgImage;
      currentOgType = 'article';
      if (currentArticle.tags && currentArticle.tags.length > 0) {
        currentKeywords = currentArticle.tags.join(', ') + ', ' + currentKeywords;
      }
      pageCanonicalPath = `/article/${currentArticle.slug || currentArticle.id}`;
      breadcrumbItems.push({ name: 'Articles', url: '/article' });
      breadcrumbItems.push({ name: currentArticle.title, url: pageCanonicalPath });
      document.title = `${currentOgTitle} | ${siteTitle}`;
    } else if (currentActiveProduct) {
      currentOgTitle = currentActiveProduct.title;
      currentOgDesc = (currentActiveProduct.description ? currentActiveProduct.description.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').substring(0, 200).trim() : '') || `${currentActiveProduct.title} - ${siteTitle}`;
      currentOgImage = currentActiveProduct.image || (currentActiveProduct.gallery && currentActiveProduct.gallery[0]) || currentOgImage;
      currentOgType = 'product';
      currentKeywords = `${currentActiveProduct.title}, ${currentActiveProduct.category || ''}, ${currentKeywords}`;
      pageCanonicalPath = `/services/${currentActiveProduct.slug || currentActiveProduct.id}`;
      breadcrumbItems.push({ name: 'Services & Solutions', url: '/services' });
      breadcrumbItems.push({ name: currentActiveProduct.title, url: pageCanonicalPath });
      document.title = `${currentOgTitle} | ${siteTitle}`;
    } else if (currentPage === 'products') {
      document.title = `Services & Solutions | ${siteTitle}`;
      currentOgTitle = `Services & Solutions | ${siteTitle}`;
      pageCanonicalPath = '/services';
      breadcrumbItems.push({ name: 'Services & Solutions', url: '/services' });
    } else if (currentPage === 'portfolio') {
      document.title = `Case Studies & Portfolio | ${siteTitle}`;
      currentOgTitle = `Case Studies & Portfolio | ${siteTitle}`;
      pageCanonicalPath = '/portfolio';
      breadcrumbItems.push({ name: 'Case Studies', url: '/portfolio' });
    } else if (currentPage === 'blog') {
      document.title = `Articles & Insights | ${siteTitle}`;
      currentOgTitle = `Articles & Insights | ${siteTitle}`;
      pageCanonicalPath = '/articles';
      breadcrumbItems.push({ name: 'Articles & Insights', url: '/articles' });
    } else if (currentPage === 'gallery') {
      document.title = `Media & Gallery | ${siteTitle}`;
      currentOgTitle = `Media & Gallery | ${siteTitle}`;
      pageCanonicalPath = '/gallery';
      breadcrumbItems.push({ name: 'Gallery', url: '/gallery' });
    } else if (currentPage === 'about') {
      document.title = `About Us | ${siteTitle}`;
      currentOgTitle = `About Us | ${siteTitle}`;
      pageCanonicalPath = '/about';
      breadcrumbItems.push({ name: 'About Us', url: '/about' });
    } else if (currentPage === 'contact') {
      document.title = `Contact Us | ${siteTitle}`;
      currentOgTitle = `Contact Us | ${siteTitle}`;
      pageCanonicalPath = '/contact';
      breadcrumbItems.push({ name: 'Contact Us', url: '/contact' });
    } else {
      // Home page
      const fullTitle = settings.seo?.metaTitle?.trim() || (settings.tagline ? `${siteTitle} - ${settings.tagline}` : siteTitle);
      document.title = fullTitle;
      currentOgTitle = fullTitle;
    }

    // Helper to set or update meta tag
    const updateMetaTag = (propertyOrName: string, value: string, isProperty = true) => {
      const attrName = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attrName}="${propertyOrName}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attrName, propertyOrName);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', value);
    };

    // Ensure image is absolute
    if (currentOgImage && !currentOgImage.startsWith('http://') && !currentOgImage.startsWith('https://')) {
      const origin = window.location.origin;
      currentOgImage = currentOgImage.startsWith('/') ? `${origin}${currentOgImage}` : `${origin}/${currentOgImage}`;
    }

    // Meta Description & Keywords
    updateMetaTag('description', currentOgDesc, false);
    updateMetaTag('keywords', currentKeywords, false);

    // Set OpenGraph Meta Tags for Social Media Sharing (WhatsApp, Facebook, LinkedIn)
    updateMetaTag('og:title', currentOgTitle, true);
    updateMetaTag('og:description', currentOgDesc, true);
    updateMetaTag('og:image', currentOgImage, true);
    updateMetaTag('og:image:secure_url', currentOgImage, true);
    updateMetaTag('og:url', window.location.href, true);
    updateMetaTag('og:type', currentOgType, true);
    updateMetaTag('og:site_name', siteTitle, true);
    updateMetaTag('og:locale', 'en_US', true);

    // Set Twitter Card Meta Tags
    updateMetaTag('twitter:card', 'summary_large_image', false);
    updateMetaTag('twitter:title', currentOgTitle, false);
    updateMetaTag('twitter:description', currentOgDesc, false);
    updateMetaTag('twitter:image', currentOgImage, false);

    // Canonical Tag Injection
    const rawCanonical = settings.seo?.canonicalUrl?.trim();
    const baseUrl = (rawCanonical && !rawCanonical.includes('example.com') && !rawCanonical.includes('localhost')) ? rawCanonical : window.location.origin;
    const finalCanonicalUrl = pageCanonicalPath ? `${baseUrl}${pageCanonicalPath}` : baseUrl;
    let canonicalLink = document.querySelector("link[rel='canonical']") as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = finalCanonicalUrl;

    // Dynamic Favicon Injection
    let faviconHref = settings.faviconUrl || settings.logoUrl;
    if (faviconHref && typeof faviconHref === 'string') {
      faviconHref = faviconHref.trim().replace(/^\/+data:/, 'data:');
      // Only set favicon link if it's a valid http(s) URL or small SVG data URI to prevent ERR_HTTP2_PROTOCOL_ERROR
      const isValidFavicon = !faviconHref.startsWith('data:') || (faviconHref.startsWith('data:image/svg+xml') && faviconHref.length < 5000);
      if (isValidFavicon) {
        let favLink = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
        if (!favLink) {
          favLink = document.createElement('link');
          favLink.rel = 'icon';
          document.head.appendChild(favLink);
        }
        favLink.href = faviconHref;

        let shortcutLink = document.getElementById('shortcut-favicon-link') as HTMLLinkElement;
        if (shortcutLink) {
          shortcutLink.href = faviconHref;
        }
      }
    }

    // 6. JSON-LD Schema.org Injection (WebSite / Organization / LocalBusiness / BreadcrumbList / NewsArticle / Product)
    const existingSchema = document.getElementById('schema-json-ld');
    if (existingSchema) existingSchema.remove();

    let schemaData: any = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${baseUrl}/#website`,
          url: baseUrl,
          name: settings.siteName,
          description: settings.seo?.metaDescription || settings.tagline,
          potentialAction: {
            '@type': 'SearchAction',
            target: `${baseUrl}/blog?search={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'Organization',
          '@id': `${baseUrl}/#organization`,
          name: settings.siteName,
          url: baseUrl,
          logo: settings.logoUrl,
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: settings.contact?.phone || settings.contact?.whatsappNumber,
            contactType: 'customer support',
            areaServed: 'ID',
            availableLanguage: ['Indonesian', 'English'],
          },
          sameAs: [
            settings.contact?.instagram,
            settings.contact?.youtube,
            settings.contact?.facebook,
          ].filter(Boolean),
        },
        {
          '@type': 'LocalBusiness',
          '@id': `${baseUrl}/#localbusiness`,
          name: settings.siteName,
          description: settings.seo?.metaDescription || settings.tagline,
          url: baseUrl,
          telephone: settings.contact?.phone || settings.contact?.whatsappNumber,
          email: settings.contact?.email,
          image: settings.seo?.ogImage || settings.logoUrl,
          priceRange: '$$',
          address: {
            '@type': 'PostalAddress',
            streetAddress: settings.contact?.address || 'Jl. Kolonel Masturi, Lembang, Bandung',
            addressLocality: 'Bandung',
            addressRegion: 'Jawa Barat',
            postalCode: '40391',
            addressCountry: 'ID',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: -6.816667,
            longitude: 107.616667,
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
              opens: '08:00',
              closes: '20:00',
            },
          ],
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${finalCanonicalUrl}/#breadcrumb`,
          itemListElement: breadcrumbItems.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`,
          })),
        },
      ],
    };

    // If viewing Article -> Add Rich NewsArticle Schema for Google Discover
    if (currentArticle) {
      const articleUrl = `${baseUrl}/artikel/${currentArticle.slug || currentArticle.id}`;
      const articleSchema = {
        '@type': 'NewsArticle',
        '@id': `${articleUrl}/#article`,
        isPartOf: { '@id': `${baseUrl}/#website` },
        headline: currentArticle.title,
        description: currentArticle.summary || currentArticle.title,
        url: articleUrl,
        datePublished: currentArticle.createdAt,
        dateModified: currentArticle.updatedAt || currentArticle.createdAt,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': articleUrl,
        },
        image: {
          '@type': 'ImageObject',
          url: currentArticle.coverImage || settings.seo?.ogImage,
          width: 1200,
          height: 675,
        },
        author: {
          '@type': 'Person',
          name: currentArticle.author || settings.siteName,
        },
        publisher: {
          '@id': `${baseUrl}/#organization`,
        },
        articleSection: currentArticle.categoryName || 'Berita & Wawasan',
        keywords: (currentArticle.tags || []).join(', '),
      };
      schemaData['@graph'].push(articleSchema);
    }

    // If viewing Product / Modal product selected -> Add Product Schema
    if (currentActiveProduct) {
      const productUrl = `${baseUrl}/produk/${currentActiveProduct.slug || currentActiveProduct.id}`;
      const productSchema = {
        '@type': 'Product',
        '@id': `${productUrl}/#product`,
        name: currentActiveProduct.title,
        description: currentActiveProduct.description,
        image: currentActiveProduct.image,
        offers: {
          '@type': 'Offer',
          url: productUrl,
          price: currentActiveProduct.price,
          priceCurrency: 'IDR',
          availability: 'https://schema.org/InStock',
        },
        category: currentActiveProduct.category,
      };
      schemaData['@graph'].push(productSchema);
    }

    const scriptSchema = document.createElement('script');
    scriptSchema.id = 'schema-json-ld';
    scriptSchema.type = 'application/ld+json';
    scriptSchema.text = JSON.stringify(schemaData);
    document.head.appendChild(scriptSchema);
  }, [settings, currentPage, currentArticleSlug, currentProductSlug, currentArticle, currentActiveProduct, selectedProduct]);

  return null;
}
